import { useCallback, useEffect, useState } from 'react';
import { Video, FileText, StickyNote, Plus, Trash2, ArrowUp, ArrowDown, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { AddContentModal } from './AddContentModal';
import { findLesson } from '@/data/lessons';
import { findSoccerLesson } from '@/data/soccerLessons';
import { findBasketballLesson } from '@/data/basketballLessons';
import { findBaseballLesson } from '@/data/baseballLessons';

/** Built-in quiz questions for curriculum lessons (coach-authored lessons have none). */
function curriculumQuestionCount(lessonId: string): number {
  if (lessonId.startsWith('custom:')) return 0;
  const found = findLesson(lessonId) || findSoccerLesson(lessonId) || findBasketballLesson(lessonId) || findBaseballLesson(lessonId);
  return found?.lesson.questions?.length ?? 0;
}

type LibraryItem = {
  id: string;
  type: 'video' | 'article' | 'note';
  title: string;
  sport: string;
};

type Row = {
  id: string;
  content_id: string;
  position: number;
  is_required: boolean;
  content: LibraryItem | null;
};

const TYPE_ICON = { video: Video, article: FileText, note: StickyNote } as const;

type Props = {
  lessonId: string;
  coachId: string;
  teamId: string;
  defaultSport?: 'football' | 'soccer' | 'basketball' | 'baseball';
  rosterPlayers?: { user_id: string; username: string | null }[];
};

/**
 * Lets a coach build a lesson out of reusable library content: add items,
 * order them, and mark each one Required or Optional. Library records are
 * never duplicated — the same video can be attached to many lessons.
 */
export function LessonContentEditor({ lessonId, coachId, teamId, defaultSport, rosterPlayers }: Props) {
  const [rows, setRows] = useState<Row[]>([]);
  const [library, setLibrary] = useState<LibraryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [picking, setPicking] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    const [itemsRes, libRes] = await Promise.all([
      supabase
        .from('lesson_content_items' as any)
        .select('id, content_id, position, is_required, coach_content:content_id (id, type, title, sport)')
        .eq('lesson_id', lessonId)
        .eq('coach_id', coachId)
        .order('position', { ascending: true }),
      supabase
        .from('coach_content' as any)
        .select('id, type, title, sport')
        .eq('coach_id', coachId)
        .order('created_at', { ascending: false }),
    ]);

    if (!itemsRes.error && itemsRes.data) {
      setRows(
        (itemsRes.data as any[]).map((r) => ({
          id: r.id,
          content_id: r.content_id,
          position: r.position,
          is_required: r.is_required,
          content: r.coach_content ?? null,
        })),
      );
    }
    if (!libRes.error && libRes.data) setLibrary(libRes.data as any);
    setLoading(false);
  }, [lessonId, coachId]);

  useEffect(() => { load(); }, [load]);

  const attach = async (contentId: string) => {
    const { error } = await supabase.from('lesson_content_items' as any).insert({
      lesson_id: lessonId,
      content_id: contentId,
      coach_id: coachId,
      team_id: teamId,
      position: rows.length + 1,
      is_required: true,
    });
    if (error) { toast.error('Could not add to lesson'); return; }
    toast.success('Added to lesson');
    setPicking(false);
    load();
  };

  const detach = async (rowId: string) => {
    const { error } = await supabase.from('lesson_content_items' as any).delete().eq('id', rowId);
    if (error) { toast.error('Could not remove'); return; }
    setRows((prev) => prev.filter((r) => r.id !== rowId));
    toast.success('Removed from lesson (still in your library)');
  };

  const toggleRequired = async (row: Row) => {
    const next = !row.is_required;
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, is_required: next } : r)));
    const { error } = await supabase
      .from('lesson_content_items' as any)
      .update({ is_required: next })
      .eq('id', row.id);
    if (error) { toast.error('Could not update'); load(); }
  };

  const move = async (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= rows.length) return;
    const next = [...rows];
    [next[index], next[target]] = [next[target], next[index]];
    const renumbered = next.map((r, i) => ({ ...r, position: i + 1 }));
    setRows(renumbered);
    await Promise.all(
      renumbered.map((r) =>
        supabase.from('lesson_content_items' as any).update({ position: r.position }).eq('id', r.id),
      ),
    );
  };

  const attachedIds = new Set(rows.map((r) => r.content_id));
  const available = library.filter((l) => !attachedIds.has(l.id));

  const quizCount = curriculumQuestionCount(lessonId);

  if (loading) return <p className="text-[11px] text-muted-foreground py-2">Loading lesson content…</p>;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-[10px] text-muted-foreground font-display font-bold uppercase tracking-wider">
          Lesson content ({rows.length + (quizCount > 0 ? 1 : 0)})
        </p>
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => setPicking((p) => !p)}>
            <Plus className="w-3 h-3 mr-1" /> From library
          </Button>
          <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => setCreating(true)}>
            New content
          </Button>
        </div>
      </div>

      {picking && (
        <div className="rounded-lg border border-border p-2 space-y-1 max-h-48 overflow-y-auto">
          {available.length === 0 ? (
            <p className="text-[11px] text-muted-foreground py-1">Everything in your library is already in this lesson.</p>
          ) : (
            available.map((l) => {
              const Icon = TYPE_ICON[l.type] ?? FileText;
              return (
                <button
                  key={l.id}
                  onClick={() => attach(l.id)}
                  className="w-full flex items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-muted/50"
                >
                  <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  <span className="text-xs truncate">{l.title}</span>
                </button>
              );
            })
          )}
        </div>
      )}

      {quizCount > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-border px-2 py-1.5">
          <Star className="w-3.5 h-3.5 text-primary shrink-0" />
          <span className="text-xs flex-1">Sportara lesson quiz · {quizCount} question{quizCount === 1 ? '' : 's'}</span>
        </div>
      )}

      {rows.length === 0 ? (
        quizCount > 0 ? null : <p className="text-[11px] text-muted-foreground py-1">
          No content in this lesson yet. Add videos, articles or resources — players will see them inside the lesson.
        </p>
      ) : (
        <div className="space-y-1">
          {rows.map((row, idx) => {
            const Icon = TYPE_ICON[row.content?.type ?? 'article'] ?? FileText;
            return (
              <div key={row.id} className="flex items-center gap-2 rounded-lg border border-border px-2 py-1.5">
                <span className="text-[10px] font-display font-bold text-muted-foreground w-4 shrink-0">{idx + 1}</span>
                <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <span className="flex-1 min-w-0 text-xs truncate">{row.content?.title ?? 'Content'}</span>
                <button
                  onClick={() => toggleRequired(row)}
                  className={`text-[9px] font-display font-bold px-1.5 py-0.5 rounded shrink-0 ${
                    row.is_required ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'
                  }`}
                  title="Toggle required / optional"
                >
                  <Star className="w-2.5 h-2.5 inline mr-0.5" />
                  {row.is_required ? 'Required' : 'Optional'}
                </button>
                <button onClick={() => move(idx, -1)} disabled={idx === 0} className="disabled:opacity-30 shrink-0">
                  <ArrowUp className="w-3.5 h-3.5 text-muted-foreground" />
                </button>
                <button onClick={() => move(idx, 1)} disabled={idx === rows.length - 1} className="disabled:opacity-30 shrink-0">
                  <ArrowDown className="w-3.5 h-3.5 text-muted-foreground" />
                </button>
                <button onClick={() => detach(row.id)} className="shrink-0">
                  <Trash2 className="w-3.5 h-3.5 text-muted-foreground hover:text-destructive" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {creating && (
        <AddContentModal
          open
          coachId={coachId}
          teamId={teamId}
          defaultSport={defaultSport}
          rosterPlayers={rosterPlayers}
          onClose={() => setCreating(false)}
          onSaved={async (newId?: string) => {
            setCreating(false);
            if (newId) await attach(newId);
            else load();
          }}
        />
      )}
    </div>
  );
}
